import { useQuery } from "@tanstack/react-query";
import { createRng } from "@/lib/seededRandom";
import { CLIENTS, EMPLOYEES, VIRTUAL_ASSISTANTS } from "@/data/mock";
import { withLatency } from "./simulate";

const rng = createRng(445566);

function computeOperationsOverview() {
  const activeClients = CLIENTS.filter((c) => c.status === "active" || c.status === "at-risk");

  const slaCompliance = Math.round(activeClients.reduce((s, c) => s + (c.slaCompliance ?? 0), 0) / activeClients.length);
  const issueRate = Math.round((activeClients.filter((c) => c.openIssues > 0).length / activeClients.length) * 1000) / 10;
  const escalations = activeClients.filter((c) => c.openIssues >= 3);
  const replacementRequests = rng.int(4, 14);

  const managerFollowUpCompliance = Math.round(
    (activeClients.filter((c) => (Date.now() - new Date(c.lastContact).getTime()) / 86400000 <= 14).length /
      activeClients.length) *
      1000,
  ) / 10;

  const trainingGapEmployees = EMPLOYEES.filter((e) => e.status === "active" && e.trainingCompletion < 60).sort(
    (a, b) => a.trainingCompletion - b.trainingCompletion,
  );

  const podMap = new Map<string, { total: number; billable: number }>();
  VIRTUAL_ASSISTANTS.forEach((va) => {
    const entry = podMap.get(va.managerName) ?? { total: 0, billable: 0 };
    entry.total += 1;
    if (va.billable) entry.billable += 1;
    podMap.set(va.managerName, entry);
  });

  const capacityHeatmap = [...podMap.entries()]
    .map(([team, v]) => ({ team, utilization: Math.round((v.billable / v.total) * 100), headcount: v.total }))
    .sort((a, b) => b.utilization - a.utilization)
    .slice(0, 12);

  return {
    slaCompliance,
    issueRate,
    replacementRequests,
    escalations,
    managerFollowUpCompliance,
    trainingGapEmployees,
    capacityHeatmap,
  };
}

export async function fetchOperationsOverview() {
  return withLatency(computeOperationsOverview(), 300);
}

export function useOperationsOverview() {
  return useQuery({ queryKey: ["operations-overview"], queryFn: fetchOperationsOverview, staleTime: 45_000 });
}
